/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { deleteTransaction } from '../../lib/rest/transactionAPIClient'
import { useMutation } from '@tanstack/react-query'
import { Button, Modal, ModalProps, Stack, Switch } from '@mantine/core'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'

export type DeleteInvoiceTransactionModalProps = ModalProps & {
  onConfirm (): void,
  transactionId: number,
}

function DeleteInvoiceTransactionModal (props: DeleteInvoiceTransactionModalProps) {
  const {
    opened,
    onConfirm,
    onClose,
    transactionId,
    ...others
  } = props

  const [isDelete, setIsDelete] = useState(false)

  useEffect(() => {
    if (opened) {
      setIsDelete(false)
    }
  }, [opened])

  const intl = useIntl()
  const deleteMutation = useMutation({
    mutationKey: ['deleteTransaction', transactionId],
    mutationFn: () => deleteTransaction(transactionId),
    onSuccess: onConfirm
  })

  const handleRemove = useCallback(() => {
    if (isDelete) {
      deleteMutation.mutate()
    } else {
      onConfirm()
    }
  }, [deleteMutation, isDelete, onConfirm])

  return (
    <Modal
      centered
      title={intl.formatMessage({
        id: 'delete',
        defaultMessage: 'Supprimer'
      })}
      {...others}
      onClose={onClose}
      opened={opened}
    >
      <Stack>
        {transactionId != null
          ? intl.formatMessage({
            id: 'do_you_want_to_remove_or_delete_transaction_from_invoice',
            defaultMessage: 'Voulez-vous retirer la transaction de la facture (elle pourra être ajoutée dans une autre facture plus tard) ou supprimer définitivement la transaction ?'
          })
          : intl.formatMessage({
            id: 'do_you_want_to_remove_transaction_from_invoice',
            defaultMessage: 'Voulez-vous supprimer la transaction ?'
          })}

        {transactionId != null
          ? <Switch
            checked={isDelete}
            onChange={() => setIsDelete(!isDelete)}
            label={intl.formatMessage({
              id: 'delete_permanently',
              defaultMessage: 'Supprimer définitivement'
            })}
          />
          : null}

        <ButtonGroup>
          <Button onClick={onClose}>
            {intl.formatMessage({
              id: 'cancel',
              defaultMessage: 'Annuler'
            })}
          </Button>
          <Button
            color={isDelete ? 'red' : undefined}
            onClick={handleRemove}
            variant="filled"
          >
            {intl.formatMessage({
              id: 'delete',
              defaultMessage: 'Supprimer'
            })}
          </Button>
        </ButtonGroup>
      </Stack>
    </Modal>
  )
}

export default DeleteInvoiceTransactionModal
